import { useState, useRef, useEffect } from 'react';
import { theme, spacing, typography, buttonStyles, mergeStyles } from '../styles/theme';

export default function UserMenu({ user, onLogout, onSettings, onUpgrade, onHowItWorks }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      {/* User Info + Hamburger Button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        cursor: 'pointer',
      }}>
        {/* User Greeting */}
        <div style={{
          textAlign: 'right',
          ...(window.innerWidth <= 480 && {
            display: 'none', // Hide on very small screens, only show hamburger
          })
        }}>
          <div style={{
            color: theme.text.inverse,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
          }}>
            {user.displayName || user.email?.split('@')[0] || 'User'}
          </div>
          <div style={{
            color: theme.brand.primary,
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.semibold,
          }}>
            {user.isPremium ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                <img 
                  src="/premium-badge.svg" 
                  alt="Premium" 
                  style={{ height: '16px', width: '16px' }}
                />
                Premium
              </span>
            ) : 'Free'}
          </div>
        </div>

        {/* Hamburger Icon */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: spacing.sm,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            transition: 'all 0.2s',
          }}
          aria-label="User menu"
        >
          <div style={{
            width: '24px',
            height: '3px',
            backgroundColor: theme.text.inverse,
            borderRadius: '2px',
            transition: 'all 0.3s',
            transform: isOpen ? 'rotate(45deg) translateY(7px)' : 'none',
          }} />
          <div style={{
            width: '24px',
            height: '3px',
            backgroundColor: theme.text.inverse,
            borderRadius: '2px',
            transition: 'all 0.3s',
            opacity: isOpen ? 0 : 1,
          }} />
          <div style={{
            width: '24px',
            height: '3px',
            backgroundColor: theme.text.inverse,
            borderRadius: '2px',
            transition: 'all 0.3s',
            transform: isOpen ? 'rotate(-45deg) translateY(-7px)' : 'none',
          }} />
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 10px)',
          right: 0,
          backgroundColor: theme.bg.dark,
          border: `2px solid ${theme.brand.primary}`,
          borderRadius: '12px',
          boxShadow: theme.shadow.large,
          padding: spacing.lg,
          minWidth: '250px',
          zIndex: 1000,
          animation: 'slideDown 0.2s ease-out',
        }}>
          {/* User Info in Dropdown */}
          <div style={{
            marginBottom: spacing.lg,
            paddingBottom: spacing.lg,
            borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
          }}>
            <div style={{
              color: theme.text.inverse,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.xs,
            }}>
              {user.displayName || 'Welcome'}
            </div>
            <div style={{
              color: theme.text.muted,
              fontSize: typography.fontSize.sm,
              marginBottom: spacing.sm,
            }}>
              {user.email}
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: user.isPremium ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              color: user.isPremium ? theme.brand.primary : theme.text.muted,
              padding: `${spacing.xs} ${spacing.sm}`,
              borderRadius: '6px',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.semibold,
            }}>
              {user.isPremium ? (
                <>
                  <img 
                    src="/premium-badge.svg" 
                    alt="Premium"
                    style={{ height: '14px', width: '14px' }}
                  />
                  Premium Account
                </>
              ) : (
                'Free Account'
              )}
            </div>
          </div>

          {/* Menu Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.sm,
          }}>
            {/* How It Works Button - MOBILE ONLY */}
            {window.innerWidth <= 768 && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  onHowItWorks();
                }}
                style={mergeStyles(
                  buttonStyles.base,
                  {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: theme.text.inverse,
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.sm,
                    justifyContent: 'flex-start',
                  }
                )}
              >
                How It Works
              </button>
            )}

            {/* Settings Button */}
            <button
              onClick={() => {
                setIsOpen(false);
                onSettings();
              }}
              style={mergeStyles(
                buttonStyles.base,
                {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: theme.text.inverse,
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  justifyContent: 'flex-start',
                }
              )}
            >
              Settings
            </button>

            {/* Upgrade Button (only for free users) */}
            {!user.isPremium && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  onUpgrade();
                }}
                style={mergeStyles(
                  buttonStyles.base,
                  buttonStyles.primary,
                  {
                    width: '100%',
                    textAlign: 'left',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: spacing.sm,
                    justifyContent: 'flex-start',
                  }
                )}
              >
                <img 
                  src="/premium-badge.svg" 
                  alt="" 
                  style={{ height: '16px', width: '16px' }}
                />
                Upgrade to Premium
              </button>
            )}

            {/* Sign Out Button */}
            <button
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              style={mergeStyles(
                buttonStyles.base,
                buttonStyles.secondary,
                {
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  justifyContent: 'flex-start',
                  color: theme.text.inverse,
                }
              )}
            >
              Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Animation */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}